import {
  Button,
  Group,
  Input,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@assetwatch/ui";
import { useState } from "react";
import type { TeamsUsersResponse } from "../../../queries/useTeamsUsersQuery";

type Team = TeamsUsersResponse["teams"][number];

type ReplaceTeamInput = {
  teamId: number;
  name: string;
  description?: string;
  isActive: boolean;
  members: { userId: number; isTeamLead: boolean }[];
};

type EditTeamModalProps = {
  team: Team | null;
  users: TeamsUsersResponse["users"];
  canManage: boolean;
  onClose: () => void;
  onSave: (input: ReplaceTeamInput) => Promise<void>;
  isSaving: boolean;
};

// A thin wrapper that remounts EditTeamForm whenever the selected team
// changes, so its fields can initialize straight from props instead of
// needing an effect to resync them.
export function EditTeamModal(props: EditTeamModalProps) {
  return <EditTeamForm key={props.team?.id ?? "none"} {...props} />;
}

function EditTeamForm({
  team,
  users,
  canManage,
  onClose,
  onSave,
  isSaving,
}: EditTeamModalProps) {
  const [name, setName] = useState(team?.name ?? "");
  const [description, setDescription] = useState(team?.description ?? "");
  const [memberIds, setMemberIds] = useState<string[]>(
    team?.members.map((member) => String(member.id)) ?? [],
  );
  const originalLead =
    team?.members.find((member) => member.isTeamLead) ?? null;
  const [leadId, setLeadId] = useState<string | null>(
    originalLead ? String(originalLead.id) : null,
  );
  const [isActive, setIsActive] = useState(team?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const originalMemberIds = new Set(
    team?.members.map((member) => String(member.id)) ?? [],
  );
  const originalLeadId = originalLead ? String(originalLead.id) : null;
  const hasChanges =
    team !== null &&
    (name.trim() !== team.name ||
      description.trim() !== (team.description ?? "") ||
      isActive !== team.isActive ||
      leadId !== originalLeadId ||
      memberIds.length !== originalMemberIds.size ||
      memberIds.some((id) => !originalMemberIds.has(id)));
  const userNamesById = new Map(
    users.map((user) => [String(user.id), user.name]),
  );

  async function save() {
    if (!team) return;
    const trimmedName = name.trim();
    if (!trimmedName) return;
    setError(null);
    try {
      await onSave({
        teamId: team.id,
        name: trimmedName,
        description: description.trim(),
        isActive,
        members: memberIds.map((id) => ({
          userId: Number(id),
          isTeamLead: id === leadId,
        })),
      });
      onClose();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The team could not be saved. Try again.",
      );
    }
  }

  return (
    <Modal
      open={team !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={team ? `Edit ${team.name}` : "Edit team"}
      description="Update the team details, manage its members, and set its status."
      size="md"
      footer={
        <Group justify="end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {canManage ? (
            <Button
              variant="primary"
              disabled={!hasChanges || !name.trim()}
              loading={isSaving}
              loadingText="Saving"
              onClick={save}
            >
              Save changes
            </Button>
          ) : null}
        </Group>
      }
    >
      {team ? (
        <Stack gap={16}>
          <Input
            label="Team name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={!canManage}
          />
          <Textarea
            label="Description"
            maxLength={500}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={!canManage}
          />
          <MultiSelect
            label={`Members (${memberIds.length})`}
            options={users.map((user) => ({
              value: String(user.id),
              label: user.name,
            }))}
            value={memberIds}
            onChange={(value) => {
              setMemberIds(value);
              if (leadId && !value.includes(leadId)) {
                setLeadId(null);
              }
            }}
            placeholder="Add members…"
            disabled={!canManage}
          />
          <Select
            searchable
            label="Team lead"
            placeholder="No team lead"
            options={memberIds.map((id) => ({
              value: id,
              label: userNamesById.get(id) ?? `User ${id}`,
            }))}
            value={leadId}
            onChange={(value) => setLeadId(value)}
            disabled={!canManage || memberIds.length === 0}
          />
          <Switch
            label="Active team"
            description="Inactive teams cannot be assigned work orders."
            checked={isActive}
            onCheckedChange={(checked) => setIsActive(checked)}
            disabled={!canManage}
          />
          {error ? <Text tone="negative">{error}</Text> : null}
        </Stack>
      ) : null}
    </Modal>
  );
}
